import AlertsHost from 'alerts/components/AlertsHost'
import AddPaymentMethodModal from 'billing/payment-methods/views/AddPaymentMethodModal'
import PaymentMethodSelectorModal from 'billing/payment-methods/views/PaymentMethodSelectorModal'
import StripeCallback from 'billing/screens/StripeCallback'
import cx from 'classnames'
import Navigation from 'components/Navigation'
import { CurrentMemberContext } from 'contexts/CurrentMemberContext'
import { ModalStackProvider } from 'contexts/ModalStackContext'
import DocumentDetailsModal from 'documents/views/DocumentDetailsModal'
import DocumentRoot from 'documents/views/DocumentRoot'
import Member from 'iam/models/Member'
import Profile from 'iam/screens/Profile'
import TeamsRoot from 'iam/screens/Teams'
import AuthenticationContainer from 'iam/views/AuthenticationContainer'
import Logout from 'iam/views/AuthenticationContainer/Logout'
import CloseJobDialog from 'jobs/views/CloseJobDialog/CloseJobAsCollaboratorDialog'
import JobRoot from 'jobs/views/JobRoot'
import { observer } from 'mobx-react'
import GlobalOpportunityDetailsSheet from 'opportunities/views/GlobalOpportunityDetailsSheet'
import OpportunitiesView from 'opportunities/views/Opportunities'
import VanishedDialog from 'opportunities/views/VanishedDialog'
import React from 'react'
import { Route, RouteComponentProps, Switch } from 'react-router'
import { RootStore } from 'RootStore'
import 'sass/base.sass'
import Dashboard from 'screens/Dashboard'
import JobCreation from '../../jobs/creation/views/JobCreation'
import styles from './App.sass'
import JobManagement from 'jobs/views/JobManagement'
import Admin from 'admin/views/Admin'
import CompleteRegistration from 'iam/views/CompleteRegistration'
import TermsModal from 'iam/views/TermsModal'
import ProPayoutsView from '../../payouts/pro/views/ProPayoutsView'
import JobCreationDialog from 'jobs/creation/views/JobCreationDialog'
import FirmAdminDashboard from '../../jobs/screens/FirmDashboard'
import OfferModal from '../../opportunities/offers/components/OfferModal'
import CouponModal from 'jobs/creation/views/JobCreation/Submission/CouponModal'
import ConsultationModal from '../../opportunities/views/ConsultationModal'
import OCRScopingJobs from '../../jobs/screens/OCRScopingJobs'
import WorkspaceInvitationModal from '../../opportunities/invitations/components/WorkspaceInvitationModal'
import UserManagement from 'user-management/views/UserManagement'
import UserDetails from 'user-management/views/UserDetails'
import Checkout from 'jobs/views/JobRoot/Checkout'
import ConfirmEmailPage from 'email-confirmation/confirm-email/ConfirmEmailPage'
import { ConfirmEmailViewModel } from '../../email-confirmation/confirm-email/ConfirmEmailViewModel'
import EmailConfirmedPage from '../../email-confirmation/email-confirmed/EmailConfirmedPage'
import { getFeatureToggleClient } from '../../infra/featureToggles'
import ClientUserManagement from 'client-user-management/views/ClientUserManagement'
import ClientUserDetails from 'client-user-management/views/ClientUserDetails'

interface Params {
  jobId?: string
  teamSlug?: string
  invitationToken?: string
}

interface Props extends RouteComponentProps<Params> {
  rootStore: RootStore
}

export default observer(function App(props: Props) {
  const { history, rootStore, match } = props

  rootStore.routerInstance = history

  const useEmailConfirmationFlow = getFeatureToggleClient().variation(
    'UseEmailConfirmationFlow',
    false,
  )

  const viewController = rootStore.rootController.current
  return (
    <Contexts
      currentMember={
        rootStore.sessionStore.hasWorkspaceSelected
          ? rootStore.sessionStore.member
          : Member.none
      }
    >
      <div className={cx(styles.container)}>
        <Switch>
          {useEmailConfirmationFlow && (
            <Route
              path="/confirm-email"
              render={(props) => (
                <ConfirmEmailPage
                  {...props}
                  vm={
                    new ConfirmEmailViewModel(
                      rootStore.routerInstance,
                      rootStore.alertsController.newAlert,
                    )
                  }
                />
              )}
            />
          )}
          {useEmailConfirmationFlow && (
            <Route
              path="/email-confirmed"
              render={(props) => (
                <EmailConfirmedPage
                  controller={rootStore.authenticationViewModel}
                  {...props}
                />
              )}
            />
          )}
          <Route
            path="/complete-registration/:token"
            render={(props) => {
              return (
                <CompleteRegistration
                  {...props}
                  vm={rootStore.completeRegistrationViewModel}
                />
              )
            }}
          />
          <AuthenticationContainer
            inviteToken={match.params.invitationToken}
            authenticationState={rootStore.authenticationViewModel}
            sessionStore={rootStore.sessionStore}
          >
            {rootStore.sessionStore.hasWorkspaceSelected && (
              <Navigation
                rootStore={rootStore}
                floating={
                  rootStore.authenticationViewModel.workspaceSelectorState
                    .isSelectorOpen
                }
              >
                <Switch>
                  <Route
                    exact
                    path="/callback/stripe-connect"
                    render={(props) => (
                      <StripeCallback
                        vm={rootStore.stripeCallbackViewModel}
                        {...props}
                      />
                    )}
                  />
                  <Route
                    path="/logout"
                    render={(props) => (
                      <Logout
                        {...props}
                        onLogout={rootStore.authStore.logout}
                      />
                    )}
                  />

                  <Route
                    exact
                    path="/"
                    render={(props) => (
                      <Dashboard {...props} rootStore={rootStore} />
                    )}
                  />
                  <Route
                    path={`/opportunities/:opportunityId?`}
                    render={(props) => (
                      <OpportunitiesView
                        {...props}
                        vm={rootStore.opportunitiesRootViewModel}
                      />
                    )}
                  />
                  <Route
                    exact
                    path={`/new/:jobId?`}
                    render={(props) => (
                      <JobCreation
                        vm={rootStore.jobCreationRootVM}
                        {...props}
                      />
                    )}
                  />
                  <Route
                    exact
                    path={`/jobs/:jobId`}
                    render={(props) => (
                      <JobRoot {...props} rootVM={rootStore.jobRootViewModel} />
                    )}
                  />
                  <Route
                    exact
                    path={`/jobs/:jobId/checkout`}
                    render={(props) => (
                      <Checkout {...props} vm={rootStore.checkoutVM} />
                    )}
                  />
                  <Route
                    exact
                    path={`/checkout`}
                    render={(props) => (
                      <Checkout {...props} vm={rootStore.checkoutVM} />
                    )}
                  />
                  <Route
                    path={`/jobs`}
                    render={(props) => (
                      <JobManagement
                        {...props}
                        vm={rootStore.jobManagementViewModel}
                      />
                    )}
                  />
                  <Route
                    path={`/documents`}
                    render={(props) =>
                      viewController && (
                        <DocumentRoot
                          {...props}
                          vm={viewController.documentRoot}
                        />
                      )
                    }
                  />
                  {viewController?.teamsController.canViewTeams && (
                    <Route
                      path={`/teams/:teamSlug?`}
                      render={(props) => (
                        <TeamsRoot
                          {...props}
                          controller={viewController.teamsController}
                        />
                      )}
                    />
                  )}
                  <Route
                    path={`/profile`}
                    render={(props) =>
                      viewController && (
                        <Profile {...props} viewController={viewController} />
                      )
                    }
                  />
                  {rootStore.proPayoutsViewModel && (
                    <Route
                      path={`/payouts`}
                      render={(props) => (
                        <ProPayoutsView
                          {...props}
                          vm={rootStore.proPayoutsViewModel!}
                        />
                      )}
                    />
                  )}

                  {rootStore.adminViewModel.canSeeAdminView && (
                    <Route
                      path={`/admin`}
                      render={(props) => (
                        <Admin {...props} vm={rootStore.adminViewModel} />
                      )}
                    />
                  )}
                  {rootStore.firmDashboardViewModel.canViewFirmDashboard && (
                    <Route
                      path={`/dashboard`}
                      render={(props) => (
                        <FirmAdminDashboard
                          {...props}
                          vm={rootStore.firmDashboardViewModel}
                        />
                      )}
                    />
                  )}

                  {rootStore.ocrScopingJobsViewModel
                    .ocrScopingJobsPermissionViewModel.canViewScopingJobs && (
                    <Route
                      path={`/scoping-jobs/:scopeWorkId?`}
                      render={(props) => (
                        <OCRScopingJobs
                          {...props}
                          vm={rootStore.ocrScopingJobsViewModel}
                        />
                      )}
                    />
                  )}
                  <Route
                    path={`/users/:userPublicId`}
                    render={(props) => (
                      <UserDetails
                        {...props}
                        vm={rootStore.userDetailsViewModel}
                      />
                    )}
                  />
                  <Route
                    path={`/users/:userPublicId`}
                    render={(props) => (
                      <UserDetails
                        {...props}
                        vm={rootStore.userDetailsViewModel}
                      />
                    )}
                  />
                  <Route
                    path={`/users`}
                    render={(props) => (
                      <UserManagement
                        {...props}
                        vm={rootStore.userManagementViewModel}
                      />
                    )}
                  />
                  <Route
                    path={`/clients/:userPublicId`}
                    render={(props) => (
                      <ClientUserDetails
                        {...props}
                        vm={rootStore.clientUserDetailsViewModel}
                      />
                    )}
                  />
                  <Route
                    path={`/clients`}
                    render={(props) => (
                      <ClientUserManagement
                        {...props}
                        vm={rootStore.clientUserManagementViewModel}
                      />
                    )}
                  />
                </Switch>
                <CloseJobDialog
                  modal={rootStore.jobRootViewModel.closeJobDialog}
                />

                <>
                  <GlobalOpportunityDetailsSheet
                    vm={
                      rootStore.opportunitiesRootViewModel
                        .opportunitiesViewModel
                    }
                  />
                  <VanishedDialog
                    vm={
                      rootStore.opportunitiesRootViewModel
                        .opportunitiesViewModel.detailsVM.vanishedDialog
                    }
                  />
                  <JobCreationDialog
                    vm={rootStore.jobCreationDialogViewModel}
                  />
                  {rootStore.jobCreationRootVM.current.submission.billing
                    .couponSelection && (
                    <CouponModal
                      vm={
                        rootStore.jobCreationRootVM.current.submission.billing
                          .couponSelection
                      }
                    />
                  )}
                </>
              </Navigation>
            )}
          </AuthenticationContainer>
        </Switch>
      </div>

      <TermsModal vm={rootStore.termsModalViewModel} />
      <AlertsHost controller={rootStore.alertsController} />
      <AddPaymentMethodModal vm={rootStore.paymentMethodsRootViewModel} />
      <PaymentMethodSelectorModal vm={rootStore.paymentMethodsRootViewModel} />
      {rootStore.documentDetailsRootViewModel.selectedDocDetailsModalVM && (
        <DocumentDetailsModal
          vm={rootStore.documentDetailsRootViewModel.selectedDocDetailsModalVM}
        />
      )}
      {rootStore.opportunitiesRootViewModel.opportunitiesViewModel.offersVM.offerModalViewModels.map(
        (vm) => (
          <OfferModal vm={vm} key={vm.id} />
        ),
      )}
      {rootStore.opportunitiesRootViewModel.workspaceOpportunitiesViewModel?.invitationsVM?.invitationModalViewModel.map(
        (vm) => <WorkspaceInvitationModal vm={vm} key={vm.id} />,
      )}
      <ConsultationModal
        vm={
          rootStore.opportunitiesRootViewModel.opportunitiesViewModel
            .consultationModalViewModel
        }
      />
    </Contexts>
  )
})

interface ContextsProps {
  currentMember: Member
  children: React.ReactNode
}

const Contexts = function Contexts({
  currentMember,

  children,
}: ContextsProps) {
  return (
    <CurrentMemberContext.Provider value={currentMember}>
      <ModalStackProvider>{children}</ModalStackProvider>
    </CurrentMemberContext.Provider>
  )
}
